import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';
import { useAuth } from '../App';
import { API } from '../utils';
import { toast } from 'sonner';
import { Camera, Sparkles, ArrowLeft, MapPin, Star, Loader2 } from 'lucide-react';

const AIMatch = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [projectDescription, setProjectDescription] = useState('');
  const [budgetRange, setBudgetRange] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleMatch = async () => {
    if (!projectDescription.trim()) {
      toast.error('Please describe your project');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/ai-match`, {
        project_description: projectDescription,
        budget_range: budgetRange,
        location: location
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setResults(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Matching failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/dashboard" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                <Camera className="w-4 h-4 text-white" strokeWidth={1.5} />
              </div>
              <span className="font-heading text-xl font-semibold">FrameFinder</span>
            </Link>
            
            <Link to="/dashboard">
              <Button variant="ghost" className="rounded-full px-6">Dashboard</Button>
            </Link>
          </div>
        </div>
      </nav>

      <div className="py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <Link to="/photographers" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to browse
          </Link>

          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-primary to-accent mb-6">
              <Sparkles className="w-8 h-8 text-white" />
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-4">AI Creative Matching</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Describe your project and let our AI find the perfect photographers and videographers for you
            </p>
          </div>

          {/* Input Form */}
          <div className="bg-secondary/30 rounded-3xl p-8 mb-12">
            <div className="space-y-6">
              <div>
                <Label className="text-base font-semibold">Describe your project *</Label>
                <Textarea
                  value={projectDescription}
                  onChange={(e) => setProjectDescription(e.target.value)}
                  placeholder="e.g., I need product photos for my new skincare line. Looking for someone who specializes in beauty and cosmetic photography. We have about 20 products that need individual shots and some lifestyle images."
                  className="mt-2 min-h-[150px] text-base"
                  data-testid="project-description-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-base font-semibold">Budget Range (optional)</Label>
                  <Input
                    value={budgetRange}
                    onChange={(e) => setBudgetRange(e.target.value)}
                    placeholder="e.g., $500 - $1000"
                    className="mt-2 h-12"
                    data-testid="budget-input"
                  />
                </div>
                <div>
                  <Label className="text-base font-semibold">Preferred Location (optional)</Label>
                  <Input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g., Los Angeles, CA"
                    className="mt-2 h-12"
                    data-testid="location-input"
                  />
                </div>
              </div>

              <Button
                onClick={handleMatch}
                className="w-full h-14 rounded-full bg-primary hover:bg-primary/90 text-lg"
                disabled={loading || !projectDescription.trim()}
                data-testid="find-matches-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Finding perfect matches...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Find My Perfect Match
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Results */}
          {results && (
            <div className="space-y-8 animate-fadeIn">
              <div className="text-center">
                <h2 className="font-heading text-2xl font-semibold mb-2">AI Recommendations</h2>
                {results.summary && (
                  <p className="text-muted-foreground">{results.summary}</p>
                )}
              </div>

              {results.recommendations?.length > 0 ? (
                <div className="space-y-6">
                  {results.recommendations.map((rec, idx) => (
                    <div 
                      key={idx} 
                      className="p-6 rounded-2xl bg-white border border-border/50 hover:border-primary/30 transition-colors"
                      data-testid={`recommendation-${idx}`}
                    >
                      <div className="flex flex-col md:flex-row gap-6">
                        {/* Profile Image */}
                        <div className="w-24 h-24 rounded-xl bg-secondary overflow-hidden flex-shrink-0">
                          {rec.profile?.portfolio_items?.[0]?.url ? (
                            <img 
                              src={rec.profile.portfolio_items[0].url} 
                              alt={rec.user_name} 
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Camera className="w-8 h-8 text-muted-foreground/30" />
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h3 className="font-semibold text-lg">{rec.user_name || rec.profile?.user_name}</h3>
                              <p className="text-muted-foreground">{rec.profile?.title}</p>
                            </div>
                            <div className="text-right">
                              <div className="text-2xl font-bold text-primary">{rec.match_score}%</div>
                              <div className="text-sm text-muted-foreground">match</div>
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-4 mb-4 text-sm">
                            {rec.profile?.city && (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <MapPin className="w-4 h-4" />
                                {rec.profile.city}
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              <span>{rec.profile?.rating?.toFixed(1) || 'New'}</span>
                            </div>
                            <span className="font-semibold">${rec.profile?.hourly_rate}/hr</span>
                          </div>

                          <p className="text-muted-foreground mb-4">{rec.reason}</p>

                          <div className="flex flex-wrap gap-2 mb-4">
                            {rec.profile?.specialties?.slice(0, 3).map((s, i) => (
                              <span key={i} className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm">
                                {s}
                              </span>
                            ))}
                          </div>

                          <Link to={`/photographers/${rec.profile?.profile_id || rec.profile_id}`}>
                            <Button className="rounded-full px-6 bg-primary hover:bg-primary/90">
                              View Profile
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 bg-secondary/30 rounded-2xl">
                  <Camera className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                  <h3 className="font-heading text-xl font-semibold mb-2">No matches found</h3>
                  <p className="text-muted-foreground mb-6">Try adjusting your project description or requirements</p>
                  <Link to="/photographers">
                    <Button variant="outline" className="rounded-full">Browse All Creatives</Button>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIMatch;
